import * as RadixDialog from "@radix-ui/react-dialog";
import type { ReactNode, RefObject } from "react";

import { OverlayContentContext, focusReturnElement, useOverlayGrant, useReturnFocusOnOpen } from "@/lib/overlay-gate";

/**
 * `DESIGN.md` § Components `sheet`: bottom-anchored, `{rounded.lg}` on the
 * top corners only, `{spacing.5}` padding, a grabber, one scrim, one
 * shadow — phone's container for the exact same content `Dialog.tsx` shows
 * on desktop (EXPERIENCE.md: "Same content, different container per
 * posture"). Built on Radix `Dialog` too, not a second hand-rolled overlay
 * mechanism — only the visual container differs from `Dialog.tsx`, so both
 * files share the identical Root/Portal/Overlay wiring, the identical
 * overlay-gate registration, and the identical `dismissible` rule.
 *
 * `max-h-[85vh] overflow-y-auto` — see `Dialog.tsx`'s own identical gap-fill
 * comment; the same unbounded-height gap existed here too (a bottom sheet
 * anchored only by `bottom-0`, with no `top`/height cap, grows upward past
 * the top of the viewport exactly like a centered dialog grows off both
 * ends), found and fixed alongside it.
 */
export interface SheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  children?: ReactNode;
  /** See `Dialog.tsx`'s own doc — identical meaning here. */
  dismissible?: boolean;
  /** See `Dialog.tsx`'s own doc — identical meaning here. */
  fallbackFocusRef?: RefObject<HTMLElement | null>;
}

export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  dismissible = true,
  fallbackFocusRef,
}: SheetProps) {
  const granted = useOverlayGrant(open);
  const returnFocusTo = useReturnFocusOnOpen(open);

  function handleOpenChange(next: boolean) {
    if (!next && !dismissible) {
      return;
    }
    onOpenChange(next);
  }

  return (
    <RadixDialog.Root open={granted} onOpenChange={handleOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-50 bg-scrim data-[state=closed]:animate-overlay-out data-[state=open]:animate-overlay-in" />
        <RadixDialog.Content
          onEscapeKeyDown={(event) => {
            if (!dismissible) {
              event.preventDefault();
            }
          }}
          onPointerDownOutside={(event) => {
            if (!dismissible) {
              event.preventDefault();
            }
          }}
          onInteractOutside={(event) => {
            if (!dismissible) {
              event.preventDefault();
            }
          }}
          onCloseAutoFocus={(event) => {
            // See `Dialog.tsx`'s identical comment — Radix's own
            // Trigger-based focus-return has nothing to restore to here
            // either, for the same reason, and the same confirm-path
            // fallback applies.
            const target = focusReturnElement(returnFocusTo, fallbackFocusRef);
            if (target) {
              event.preventDefault();
              target.focus();
            }
          }}
          {...(description === undefined ? { "aria-describedby": undefined } : {})}
          className="fixed inset-x-0 bottom-0 z-50 max-h-[85vh] overflow-y-auto rounded-t-lg bg-surface p-5 shadow-overlay data-[state=closed]:animate-sheet-out data-[state=open]:animate-sheet-in"
        >
          <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-border-strong" aria-hidden="true" />
          <RadixDialog.Title className="text-title font-semibold text-ink">{title}</RadixDialog.Title>
          {description !== undefined ? (
            <RadixDialog.Description className="mt-2 text-body text-ink-secondary">
              {description}
            </RadixDialog.Description>
          ) : null}
          {/* See `Dialog.tsx`'s identical wiring — `OverlayContentContext`'s
           * own doc comment has the full reasoning. */}
          <OverlayContentContext.Provider value={true}>{children}</OverlayContentContext.Provider>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
